import * as THREE from 'three';
import { Line2 } from 'three/examples/jsm/lines/Line2.js';
import { LineGeometry } from 'three/examples/jsm/lines/LineGeometry.js';
import { LineMaterial } from 'three/examples/jsm/lines/LineMaterial.js';
import { SphereMarker } from './SphereMarker';
import { Viewer } from '../viewer/viewer';
import { DefaultColors } from '../defines';
import { calculatePolygonArea2D, calculatePolygonPerimeter } from './GeometryUtils';
import { TextSprite } from '../TextSprite';
import { createAreaLabel, createVolumeLabel } from './LabelUtils';
import { MeasureTools, type AnnotationDomain } from './Measure';
import type { Subscription, Coordinate, RoomPlane } from '@pointorama/potree-shared';
import { getAnnotationColorProperty, getAnnotationVisibleProperty, getAnnotationShowLengthsProperty, getAnnotationShowAreaProperty, getAnnotationShowVolumeProperty, getRoomPolygonProperty, getRoomBaseProperty, getRoomCeilingProperty } from '@pointorama/potree-shared';
import { Utils } from '../utils';
import type { EditableShape, EditableShapeKind, EditEdge } from '../personas/tools/implementations/line-tool/EditableShape';

export interface Plane {
  position: THREE.Vector3;
  normal: THREE.Vector3;
}

/**
 * PointoramaRoom represents a 3D room defined by:
 * - A polygon (array of points defining the room boundary)
 * - A base plane (position + normal) - bottom of the room
 * - A ceiling plane (position + normal) - top of the room
 */
export class PointoramaRoom extends THREE.Object3D implements EditableShape {
  private _identifier = '';
  groupName = '';
  groupIdentifier: string | null = null;

  get identifier(): string {
    return this._identifier;
  }

  set identifier(value: string) {
    this._identifier = value;
    if (value) {
      this.loadReactiveProperties();
    }
  }
  finished = false;
  selected = false;
  boundingSphere?: THREE.Sphere; // need a boundingSphere for zoomTo() to function properly
  type: MeasureTools = MeasureTools.AREA; // Rooms behave like area measurements for editing
  readonly domain: AnnotationDomain = 'room'; // Edit ownership — only room tools may edit
  lastMovedPoint: number | null = null; // Track last edited point for MeasureEngine drag handling
  needsUpdate = false; // Track if visuals need updating (used by MeasureEngine)

  private polygon: THREE.Vector3[];
  private base: Plane;
  private ceiling: Plane;

  // Visual elements
  public spheres: SphereMarker[];
  private lineGeometry: LineGeometry;
  private line: Line2;
  public lineMaterial: LineMaterial; // Public for depth fade updates
  public backgroundLine: Line2; // Public for InputHandler line intersection detection
  public backgroundLineTouch: Line2; // Public for touch device line intersection
  public backgroundLineMaterial: LineMaterial; // Public for depth fade updates
  public backgroundLineTouchMaterial: LineMaterial; // Public for depth fade updates
  // Translucent polygon fill shown while the room is SELECTED, so the whole
  // room reads as selected — the fat background line alone only marks the
  // boundary. Built lazily on first select.
  private fillMesh: THREE.Mesh | null = null;
  private fillGeometry: THREE.ShapeGeometry | null = null;
  private fillMaterial: THREE.MeshBasicMaterial | null = null;
  private color: THREE.Color;
  private viewer: Viewer;

  // Labels
  private _showDistances: boolean;
  private _showArea: boolean;
  private _showVolume: boolean;
  public edgeLabels: TextSprite[]; // Public for depth fade updates
  public areaLabel: TextSprite; // Public for depth fade updates
  public volumeLabel: TextSprite; // Public for depth fade updates

  // Unit conversion
  private lengthUnit?: { unitspermeter: number } | null;
  private lengthUnitDisplay?: { unitspermeter: number; code: string } | null;

  // Reactive property subscriptions
  private _colorSubscription?: Subscription;
  private _visibleSubscription?: Subscription;
  private _showLengthLabelsSubscription?: Subscription;
  private _showAreaLabelsSubscription?: Subscription;
  private _showVolumeLabelsSubscription?: Subscription;
  private _polygonSubscription?: Subscription;
  private _baseSubscription?: Subscription;
  private _ceilingSubscription?: Subscription;

  // Edit mode state
  private _isInEditMode = false;
  private _editModeOriginalPolygon: THREE.Vector3[] | null = null;

  constructor(viewer: Viewer, args: { polygon?: THREE.Vector3[]; base?: Plane; ceiling?: Plane; color?: THREE.Color } = {}) {
    super();

    this.polygon = args.polygon ?? [];
    this.base = args.base ?? { position: new THREE.Vector3(), normal: new THREE.Vector3(0, 0, 1) };
    this.ceiling = args.ceiling ?? { position: new THREE.Vector3(), normal: new THREE.Vector3(0, 0, -1) };
    this.viewer = viewer;
    this.color = args.color || DefaultColors.OBJECTS_THREE;
    this.spheres = [];

    // Create line geometry
    this.lineGeometry = new LineGeometry();
    this.lineMaterial = new LineMaterial({
      color: this.color,
      linewidth: 1,
      resolution: new THREE.Vector2(1000, 1000),
      depthTest: false,
      transparent: true,
    });
    this.line = new Line2(this.lineGeometry, this.lineMaterial);
    this.add(this.line);

    // Create background line for selection
    this.backgroundLineMaterial = new LineMaterial({
      color: this.color,
      linewidth: 15,
      resolution: new THREE.Vector2(1000, 1000),
      transparent: true,
      depthTest: false,
      opacity: this.selected ? 0.25 : 0.0,
    });
    this.backgroundLine = new Line2(this.lineGeometry, this.backgroundLineMaterial);
    this.backgroundLine.name = 'RoomBackgroundLine';
    this.add(this.backgroundLine);

    // Create background line for touch interaction (wider and invisible)
    this.backgroundLineTouchMaterial = new LineMaterial({
      color: this.color,
      linewidth: 30,
      resolution: new THREE.Vector2(1000, 1000),
      transparent: true,
      opacity: 0.0,
      depthTest: false,
    });
    this.backgroundLineTouch = new Line2(this.lineGeometry, this.backgroundLineTouchMaterial);
    this.backgroundLineTouch.name = 'RoomBackgroundLineTouch';
    this.add(this.backgroundLineTouch);

    // labels
    this._showDistances = true;
    this._showArea = false;
    this._showVolume = false;
    this.edgeLabels = [];
    this.areaLabel = createAreaLabel();
    this.volumeLabel = createVolumeLabel();
    this.add(this.areaLabel);
    this.add(this.volumeLabel);

    // Load reactive property subscriptions (creates visuals + loads geometry from property system if not provided)
    this.loadReactiveProperties(args);
  }

  /**
   * Subscribe to reactive properties (annotation_color, annotation_visible).
   * Uses init-skip pattern to avoid overwriting constructor args on first callback.
   */
  private coordToVec3(c: Coordinate): THREE.Vector3 {
    return new THREE.Vector3(c.x, c.y, c.z);
  }

  private roomPlaneToPlane(rp: RoomPlane): Plane {
    return { position: this.coordToVec3(rp.position), normal: this.coordToVec3(rp.normal) };
  }

  loadReactiveProperties(args: { polygon?: THREE.Vector3[]; base?: Plane; ceiling?: Plane; color?: THREE.Color; visible?: boolean } = {}): void {
    if (!this.identifier) return;

    try {
      this._colorSubscription?.unsubscribe();
      this._visibleSubscription?.unsubscribe();
      this._showLengthLabelsSubscription?.unsubscribe();
      this._showAreaLabelsSubscription?.unsubscribe();
      this._showVolumeLabelsSubscription?.unsubscribe();
      this._polygonSubscription?.unsubscribe();
      this._baseSubscription?.unsubscribe();
      this._ceilingSubscription?.unsubscribe();

      // --- Geometry (polygon, base, ceiling) — each loaded independently ---
      const polygonProp = getRoomPolygonProperty(this.identifier);
      const baseProp = getRoomBaseProperty(this.identifier);
      const ceilingProp = getRoomCeilingProperty(this.identifier);

      if (!args.polygon) {
        const coords = polygonProp.value;
        if (coords.length >= 3) {
          this.polygon = coords.map((c) => this.coordToVec3(c));
        }
      }
      if (!args.base) {
        this.base = this.roomPlaneToPlane(baseProp.value);
      }
      if (!args.ceiling) {
        this.ceiling = this.roomPlaneToPlane(ceilingProp.value);
      }

      if (this.polygon.length >= 3) {
        this.createVisualElements();
      }

      this._polygonSubscription = polygonProp.subscribeToChanges((coords) => {
        if (coords.length >= 3) {
          this.polygon = coords.map((c) => this.coordToVec3(c));
          this.updateVisuals();
        }
      });
      this._baseSubscription = baseProp.subscribeToChanges((value) => {
        this.base = this.roomPlaneToPlane(value);
        this.updateBoundingSphere();
      });
      this._ceilingSubscription = ceilingProp.subscribeToChanges((value) => {
        this.ceiling = this.roomPlaneToPlane(value);
        this.updateBoundingSphere();
      });

      // --- Color ---
      const colorProperty = getAnnotationColorProperty(this.identifier);
      if (args.color !== undefined) colorProperty.value = '#' + this.color.getHexString();
      else this.setColor(new THREE.Color(colorProperty.value));
      this._colorSubscription = colorProperty.subscribeToChanges((value) => {
        this.setColor(new THREE.Color(value));
      });

      // --- Visible ---
      const visibleProperty = getAnnotationVisibleProperty(this.identifier);
      if (args.visible !== undefined) visibleProperty.value = this.visible;
      else this.visible = visibleProperty.value;
      this._visibleSubscription = visibleProperty.subscribeToChanges((value) => {
        this.visible = value;
      });

      // --- Label visibility (no constructor args — always read) ---
      // Shared with Distance/Area annotations via annotation_show_* identifiers.
      const lengthProp = getAnnotationShowLengthsProperty(this.identifier);
      this.showDistances = lengthProp.value;
      this._showLengthLabelsSubscription = lengthProp.subscribeToChanges((value) => {
        this.showDistances = value;
      });

      const areaProp = getAnnotationShowAreaProperty(this.identifier);
      this.showArea = areaProp.value;
      this._showAreaLabelsSubscription = areaProp.subscribeToChanges((value) => {
        this.showArea = value;
      });

      const volumeProp = getAnnotationShowVolumeProperty(this.identifier);
      this.showVolume = volumeProp.value;
      this._showVolumeLabelsSubscription = volumeProp.subscribeToChanges((value) => {
        this.showVolume = value;
      });
    } catch {
      // Global property service not initialized yet
    }
  }

  getPolygon(): THREE.Vector3[] {
    return this.polygon;
  }

  setPolygon(polygon: THREE.Vector3[]): void {
    if (polygon.length < 3) {
      throw new Error('Polygon must have at least 3 points');
    }
    this.polygon = polygon;
    this.updateVisuals();
  }

  /**
   * Get points array compatible with MeasureEngine editing interface
   */
  get points(): Array<{ position: THREE.Vector3 }> {
    return this.polygon.map((pos) => ({ position: pos }));
  }

  /**
   * Set point data at index (used by MeasureEngine drag handling)
   */
  setPointData(index: number, position: THREE.Vector3): void {
    if (index >= 0 && index < this.polygon.length) {
      this.polygon[index].copy(position);

      // If moving the first point (origin), update room position and all sphere positions
      if (index === 0) {
        this.position.copy(this.polygon[0]);
        // Update all spheres to be relative to new origin
        for (let i = 0; i < this.spheres.length; i++) {
          const relativePosition = this.polygon[i].clone().sub(this.polygon[0]);
          this.spheres[i].position.copy(relativePosition);
        }
      } else {
        // Just update the sphere at this index
        const relativePosition = position.clone().sub(this.polygon[0]);
        this.spheres[index].position.copy(relativePosition);
      }

      // Update lines and labels without recreating spheres
      this.updateLinePositions();
      this.updateLabelPositions();
      this.updateBoundingSphere();
    }
  }

  /**
   * Add a new marker/point at the specified index (for edge dragging compatibility)
   * This allows adding points by clicking and dragging on edges
   */
  addMarker(point: THREE.Vector3, index?: number): void {
    const insertIndex = index !== undefined ? index : this.polygon.length;

    // Insert the point into the polygon
    this.polygon.splice(insertIndex, 0, point.clone());

    // Create and insert a new sphere
    const sphere = new SphereMarker(this.viewer);
    sphere.setColor(this.color);
    sphere.setScaleFactor(this.viewer.scene.roomSphereScale);
    const relativePosition = point.clone().sub(this.polygon[0]);
    sphere.position.copy(relativePosition);

    this.spheres.splice(insertIndex, 0, sphere);
    this.add(sphere);

    // Add a new edge label
    this.addEdgeLabel();

    // Mark for update
    this.needsUpdate = true;

    // Update all visuals
    this.updateVisuals();
  }

  /**
   * Remove a marker/point at the specified index (for right-click delete compatibility)
   * This allows removing points by right-clicking on them
   */
  removeMarker(index: number): void {
    if (index < 0 || index >= this.polygon.length) return;

    // Remove the point from the polygon
    this.polygon.splice(index, 1);

    // Remove and dispose the sphere
    const sphere = this.spheres[index];
    this.remove(sphere);
    this.spheres.splice(index, 1);

    // Remove an edge label
    const label = this.edgeLabels.pop();
    if (label) this.remove(label);

    // If we removed the first point, update the room position and all sphere positions
    if (index === 0 && this.polygon.length > 0) {
      this.position.copy(this.polygon[0]);
      for (let i = 0; i < this.spheres.length; i++) {
        const relativePosition = this.polygon[i].clone().sub(this.polygon[0]);
        this.spheres[i].position.copy(relativePosition);
      }
    }

    // Mark for update
    this.needsUpdate = true;

    // Update all visuals
    this.updateVisuals();
  }

  /**
   * Check if a marker can be removed (must have at least 3 points)
   */
  canRemoveMarker(): boolean {
    return this.polygon.length > 3;
  }

  /**
   * Get the index of a sphere in the spheres array
   */
  getSphereIndex(sphere: SphereMarker): number {
    return this.spheres.indexOf(sphere);
  }

  /**
   * Get the closest point on a line segment to a given point
   * This enables perpendicular snapping when using the measuring tool
   */
  getClosestPointOnLineSegment(pointIndex: number, point: THREE.Vector3): THREE.Vector3 {
    const segmentDirection = new THREE.Vector3().subVectors(this.polygon[pointIndex], this.polygon[(pointIndex + 1) % this.polygon.length]).normalize();
    const v = new THREE.Vector3().subVectors(point, this.polygon[pointIndex]);
    const distance = v.dot(segmentDirection);

    return this.polygon[pointIndex].clone().add(segmentDirection.multiplyScalar(distance));
  }

  getBase(): Plane {
    return this.base;
  }

  setBase(base: Plane): void {
    this.base = base;
    this.updateBoundingSphere();
  }

  getCeiling(): Plane {
    return this.ceiling;
  }

  setCeiling(ceiling: Plane): void {
    this.ceiling = ceiling;
    this.updateBoundingSphere();
  }

  /**
   * Calculate the perimeter of the room's base polygon
   */
  getPerimeter(): number {
    return calculatePolygonPerimeter(this.polygon, true);
  }

  /**
   * Calculate the area of the room's base polygon
   * Uses the same algorithm as Measure.getArea() for consistency
   */
  getArea(): number {
    return calculatePolygonArea2D(this.polygon);
  }

  /**
   * Calculate the volume of the room (area × height)
   */
  getVolume(): number {
    const area = this.getArea();
    const height = Math.abs(this.ceiling.position.z - this.base.position.z);
    return area * height;
  }

  /**
   * Add a label for displaying edge/segment length
   */
  private addEdgeLabel(): void {
    const edgeLabel = new TextSprite('');
    edgeLabel.setBorderColor({ r: 0, g: 0, b: 0, a: 1.0 });
    edgeLabel.setBackgroundColor({ r: 0, g: 0, b: 0, a: 0.3 });
    edgeLabel.material.depthTest = false;
    edgeLabel.visible = false;
    edgeLabel.fontsize = 16;
    this.edgeLabels.push(edgeLabel);
    this.add(edgeLabel);
  }

  /**
   * Calculate and update the bounding sphere based on polygon, base, and ceiling
   */
  private updateBoundingSphere(): void {
    if (this.polygon.length === 0) {
      this.boundingSphere = undefined;
      return;
    }

    // Get relative polygon points (relative to room position)
    const relativePoints = this.polygon.map((p) => p.clone().sub(this.polygon[0]));

    // Include base and ceiling positions to ensure the sphere encompasses the full 3D room volume
    if (this.base && this.ceiling) {
      const relativeBase = this.base.position.clone().sub(this.polygon[0]);
      const relativeCeiling = this.ceiling.position.clone().sub(this.polygon[0]);
      relativePoints.push(relativeBase);
      relativePoints.push(relativeCeiling);
    }

    // Create bounding box first, then derive sphere from it
    const boundingBox = new THREE.Box3().setFromPoints(relativePoints);
    this.boundingSphere = boundingBox.getBoundingSphere(new THREE.Sphere());
  }

  /**
   * Create visual elements (spheres and lines) for the room polygon
   */
  private createVisualElements(): void {
    // Position the room at the first polygon point
    if (this.polygon.length > 0) {
      this.position.copy(this.polygon[0]);
    }

    // Create spheres for each polygon point
    for (let i = 0; i < this.polygon.length; i++) {
      const sphere = new SphereMarker(this.viewer);
      sphere.setColor(this.color);
      sphere.setScaleFactor(this.viewer.scene.roomSphereScale);
      const relativePosition = this.polygon[i].clone().sub(this.polygon[0]);
      sphere.position.copy(relativePosition);
      this.spheres.push(sphere);
      this.add(sphere);
    }

    // Create edge labels for each line segment (including closing edge)
    for (let i = 0; i < this.polygon.length; i++) {
      this.addEdgeLabel();
    }

    // Update line positions to create a closed polygon
    this.updateLinePositions();

    // Update all label positions
    this.updateLabelPositions();

    // Calculate bounding sphere
    this.updateBoundingSphere();

    // Scale labels immediately so they don't appear at default size
    this.updateScales();
  }

  /**
   * Update the positions of the lines connecting the polygon points
   */
  private updateLinePositions(): void {
    // Create vertices array: all polygon points relative to the first point, then close the loop
    const vertices = this.polygon.map((point) => point.clone().sub(this.polygon[0]).toArray()).flat();

    // Close the polygon by adding the first point again
    vertices.push(0, 0, 0);

    // Dispose old buffers before setting new positions to force a full geometry rebuild.
    // This ensures the line geometry properly updates after editing.
    this.lineGeometry.dispose();
    this.lineGeometry.setPositions(vertices);

    // Required for Line2 to render properly
    this.line.computeLineDistances();
    this.backgroundLine.computeLineDistances();
    this.backgroundLineTouch.computeLineDistances();

    // Mark materials as needing update
    this.lineMaterial.needsUpdate = true;
    this.backgroundLineMaterial.needsUpdate = true;
    this.backgroundLineTouchMaterial.needsUpdate = true;

    // Every polygon change funnels through here — keep the selection fill
    // matching the new shape (no-op while not selected).
    this.updateSelectionFill();
  }

  /**
   * Update the position and text of an edge label for a specific line segment
   * Uses relative coordinates to avoid floating-point precision issues with large world coordinates.
   */
  private updateEdgeLabelPosition(index: number): void {
    const edgeLabel = this.edgeLabels[index];
    if (!edgeLabel) return;

    const lastIndex = this.polygon.length - 1;
    const nextIndex = (index + 1) % (lastIndex + 1);

    const point = this.polygon[index];
    const nextPoint = this.polygon[nextIndex];
    const origin = this.polygon[0];

    // Calculate center point of the edge using RELATIVE coordinates first
    // to avoid precision issues with large world coordinates
    const relativePoint = point.clone().sub(origin);
    const relativeNextPoint = nextPoint.clone().sub(origin);
    const center = relativePoint.add(relativeNextPoint).multiplyScalar(0.5);

    // Calculate distance between points
    const distance = point.distanceTo(nextPoint);

    edgeLabel.position.copy(center);

    // Apply unit conversion if units are specified
    let displayDistance = distance;
    let suffix = '';
    if (this.lengthUnit != null && this.lengthUnitDisplay != null) {
      displayDistance = (distance / this.lengthUnit.unitspermeter) * this.lengthUnitDisplay.unitspermeter;
      suffix = this.lengthUnitDisplay.code;
    }

    // Format and display the distance
    const txtLength = displayDistance.toFixed(2);
    edgeLabel.setText(`${txtLength} ${suffix}`);
    edgeLabel.visible = this._showDistances && distance > 0;
  }

  /**
   * Update the position and text of area and volume labels
   * Uses relative coordinates to avoid floating-point precision issues with large world coordinates.
   */
  private updateAreaAndVolumeLabelPosition(): void {
    // Calculate centroid using RELATIVE coordinates to avoid precision issues
    const origin = this.polygon[0];
    const centroid = new THREE.Vector3();
    for (let i = 0; i < this.polygon.length; i++) {
      // Add relative position (subtract origin first, then add)
      centroid.add(this.polygon[i].clone().sub(origin));
    }
    centroid.divideScalar(this.polygon.length);

    // Update area label
    if (this.areaLabel) {
      this.areaLabel.position.copy(centroid);
      this.areaLabel.visible = this._showArea && this.polygon.length >= 3;

      let area = this.getArea();
      let suffix = '';
      if (this.lengthUnit != null && this.lengthUnitDisplay != null) {
        area = (area / Math.pow(this.lengthUnit.unitspermeter, 2)) * Math.pow(this.lengthUnitDisplay.unitspermeter, 2);
        suffix = this.lengthUnitDisplay.code;
      }

      const txtArea = area.toFixed(1);
      const msg = `${txtArea} ${suffix}\u00B2`;
      this.areaLabel.setText(msg);
    }

    // Update volume label (position it below the area label)
    if (this.volumeLabel) {
      const labelOffset = this.areaLabel?.visible ? centroid.length() / 5 : 0;
      const position = new THREE.Vector3(centroid.x, centroid.y - labelOffset, centroid.z);
      this.volumeLabel.position.copy(position);
      this.volumeLabel.visible = this._showVolume;

      let volume = this.getVolume();
      let suffix = '';
      if (this.lengthUnit != null && this.lengthUnitDisplay != null) {
        volume = (volume / Math.pow(this.lengthUnit.unitspermeter, 3)) * Math.pow(this.lengthUnitDisplay.unitspermeter, 3);
        suffix = this.lengthUnitDisplay.code;
      }

      const txtVolume = volume.toFixed(1);
      const msg = `${txtVolume} ${suffix}\u00B3`;
      this.volumeLabel.setText(msg);
    }
  }

  /**
   * Update all label positions
   */
  private updateLabelPositions(): void {
    // Don't update labels when in edit mode (they should stay hidden)
    if (this._isInEditMode) return;

    // Update edge labels for each line segment
    for (let i = 0; i < this.polygon.length; i++) {
      if (this.edgeLabels[i]) {
        this.updateEdgeLabelPosition(i);
      }
    }

    // Update area and volume labels
    this.updateAreaAndVolumeLabelPosition();
  }

  /**
   * Update visual elements when polygon changes
   */
  private updateVisuals(): void {
    // Remove old spheres
    this.spheres.forEach((sphere) => this.remove(sphere));
    this.spheres = [];

    // Remove old edge labels
    this.edgeLabels.forEach((label) => this.remove(label));
    this.edgeLabels = [];

    // Recreate visual elements
    this.createVisualElements();

    // Update bounding sphere
    this.updateBoundingSphere();
  }

  /**
   * Get the color of the room visualization
   */
  getColor(): THREE.Color {
    return this.color;
  }

  /**
   * Set the color of the room visualization
   */
  setColor(color: THREE.Color): void {
    this.color = color;
    this.lineMaterial.color = color;
    this.backgroundLineMaterial.color = color;
    if (this.fillMaterial) this.fillMaterial.color = color;
    this.spheres.forEach((sphere) => sphere.setColor(color));
  }

  /**
   * Highlight the room (for selection): fat boundary line + polygon fill.
   */
  select(): void {
    this.selected = true;
    this.backgroundLineMaterial.opacity = 0.25;
    this.updateSelectionFill();
  }

  /**
   * Remove highlight from the room
   */
  deselect(): void {
    this.selected = false;
    this.backgroundLineMaterial.opacity = 0.0;
    if (this.fillMesh) this.fillMesh.visible = false;
  }

  /**
   * Build/refresh the selection fill from the current polygon. Triangulated
   * as a THREE.Shape in the XY plane at the first point's height, with
   * vertices relative to `polygon[0]` — the same localization the lines use
   * (the room Object3D sits at `polygon[0]`, keeping float32 geometry
   * precise at UTM-scale coordinates). Hidden while not selected.
   */
  private updateSelectionFill(): void {
    if (!this.selected || this.polygon.length < 3) {
      if (this.fillMesh) this.fillMesh.visible = false;
      return;
    }
    const shape = new THREE.Shape();
    shape.moveTo(0, 0);
    for (let i = 1; i < this.polygon.length; i++) {
      shape.lineTo(this.polygon[i].x - this.polygon[0].x, this.polygon[i].y - this.polygon[0].y);
    }
    shape.closePath();

    const geometry = new THREE.ShapeGeometry(shape);
    if (!this.fillMesh) {
      this.fillMaterial = new THREE.MeshBasicMaterial({
        color: this.color,
        transparent: true,
        opacity: 0.2,
        depthTest: false,
        side: THREE.DoubleSide,
      });
      this.fillMesh = new THREE.Mesh(geometry, this.fillMaterial);
      // Under the boundary lines and markers (they default to 0), over the
      // opaque point cloud — depthTest is off, so order decides.
      this.fillMesh.renderOrder = -1;
      this.add(this.fillMesh);
    } else {
      this.fillGeometry?.dispose();
      this.fillMesh.geometry = geometry;
    }
    this.fillGeometry = geometry;
    this.fillMesh.visible = true;
  }

  /**
   * Update label scales based on camera distance
   * Makes labels maintain constant screen-space size regardless of camera distance
   */
  /** Public so the per-RenderView render path can rescale labels for a
   *  widget's camera before its render runs. With no args, reads the
   *  active main camera + main renderer size (original behavior). */
  updateScales(cameraOverride?: THREE.PerspectiveCamera | THREE.OrthographicCamera, widthOverride?: number, heightOverride?: number): void {
    const camera = cameraOverride ?? this.viewer.scene.getActiveCamera();

    // Check if length units changed and trigger label position update if needed
    if (this.lengthUnit !== this.viewer.lengthUnit || this.lengthUnitDisplay !== this.viewer.lengthUnitDisplay) {
      this.lengthUnit = this.viewer.lengthUnit;
      this.lengthUnitDisplay = this.viewer.lengthUnitDisplay;
      this.updateLabelPositions();
    }

    let clientWidth: number, clientHeight: number;
    if (widthOverride !== undefined && heightOverride !== undefined) {
      clientWidth = widthOverride;
      clientHeight = heightOverride;
    } else {
      const renderAreaSize = this.viewer.renderer.getSize(new THREE.Vector2());
      clientWidth = renderAreaSize.width;
      clientHeight = renderAreaSize.height;
    }

    // Scale edge labels based on camera distance
    for (const label of this.edgeLabels) {
      const distance = camera.position.distanceTo(label.getWorldPosition(new THREE.Vector3()));
      const pr = Utils.projectedRadius(1, camera, distance, clientWidth, clientHeight);
      const scale = 70 / pr;
      label.scale.set(scale, scale, scale);
    }

    // Scale area label based on camera distance
    if (this.areaLabel) {
      const distance = camera.position.distanceTo(this.areaLabel.getWorldPosition(new THREE.Vector3()));
      const pr = Utils.projectedRadius(1, camera, distance, clientWidth, clientHeight);
      const scale = 70 / pr;
      this.areaLabel.scale.set(scale, scale, scale);
    }

    // Scale volume label based on camera distance
    if (this.volumeLabel) {
      const distance = camera.position.distanceTo(this.volumeLabel.getWorldPosition(new THREE.Vector3()));
      const pr = Utils.projectedRadius(1, camera, distance, clientWidth, clientHeight);
      const scale = 70 / pr;
      this.volumeLabel.scale.set(scale, scale, scale);
    }

    // Sphere-marker rescaling is centralized in `Viewer.rescaleLabelsForView`
    // — runs once per render pass for all sphere owners (measurements,
    // rooms, wallSketches), so we don't duplicate the loop here.
  }

  /**
   * Update the room's visual elements (called by the viewer's render loop)
   * CRITICAL: This updates the line material resolution to match the current canvas size
   */
  update(): void {
    // Update line material resolution to match current canvas size
    const renderAreaSize = this.viewer.renderer.getSize(new THREE.Vector2());
    this.lineMaterial.resolution.set(renderAreaSize.width, renderAreaSize.height);
    this.backgroundLineMaterial.resolution.set(renderAreaSize.width, renderAreaSize.height);
    this.backgroundLineTouchMaterial.resolution.set(renderAreaSize.width, renderAreaSize.height);

    // Update label positions
    this.updateLabelPositions();

    // Update label scales based on camera distance
    this.updateScales();
  }

  /**
   * Clone the room
   */
  clone(recursive?: boolean): this {
    const polygonClone = this.polygon.map((p) => p.clone());
    const baseClone: Plane = {
      position: this.base.position.clone(),
      normal: this.base.normal.clone(),
    };
    const ceilingClone: Plane = {
      position: this.ceiling.position.clone(),
      normal: this.ceiling.normal.clone(),
    };

    const cloned = new PointoramaRoom(this.viewer, { polygon: polygonClone, base: baseClone, ceiling: ceilingClone, color: this.color });

    // Copy Object3D properties
    cloned.copy(this, recursive);

    return cloned as this;
  }

  // Label visibility getters and setters
  get showDistances(): boolean {
    return this._showDistances;
  }

  set showDistances(value: boolean) {
    if (this._showDistances !== value) {
      this._showDistances = value;
      this.updateLabelPositions();
    }
  }

  get showArea(): boolean {
    return this._showArea;
  }

  set showArea(value: boolean) {
    if (this._showArea !== value) {
      this._showArea = value;
      this.updateLabelPositions();
    }
  }

  get showVolume(): boolean {
    return this._showVolume;
  }

  set showVolume(value: boolean) {
    if (this._showVolume !== value) {
      this._showVolume = value;
      this.updateLabelPositions();
    }
  }

  /**
   * Check if the room is currently in edit mode
   */
  get isInEditMode(): boolean {
    return this._isInEditMode;
  }

  // ─── EditableShape ──────────────────────────────────────────────────

  getKind(): EditableShapeKind {
    return 'room';
  }

  /**
   * Enter edit mode: hides room visualization and returns edge data for prompts.
   * The room stores its original polygon for potential restoration.
   * @returns Array of EditEdge objects representing each edge of the polygon
   */
  enterEditMode(): EditEdge[] {
    if (this._isInEditMode) {
      // Already in edit mode, just return current edges
      return this.getEdges();
    }

    // Store original polygon for potential cancel/restore
    this._editModeOriginalPolygon = this.polygon.map((p) => p.clone());
    this._isInEditMode = true;

    // Hide all visual elements
    this.line.visible = false;
    this.backgroundLine.visible = false;
    this.backgroundLineTouch.visible = false;
    this.spheres.forEach((sphere) => (sphere.visible = false));
    this.edgeLabels.forEach((label) => (label.visible = false));
    if (this.areaLabel) this.areaLabel.visible = false;
    if (this.volumeLabel) this.volumeLabel.visible = false;

    // Return edges for conversion to prompts
    return this.getEdges();
  }

  /**
   * Get the edges of the polygon as EditEdge objects.
   * Each edge connects consecutive vertices (including closing edge).
   */
  getEdges(): EditEdge[] {
    const edges: EditEdge[] = [];
    const n = this.polygon.length;

    for (let i = 0; i < n; i++) {
      const start = this.polygon[i];
      const end = this.polygon[(i + 1) % n];

      const midpoint = new THREE.Vector3().addVectors(start, end).multiplyScalar(0.5);
      const direction = new THREE.Vector3().subVectors(end, start).normalize();

      edges.push({
        midpoint: midpoint.clone(),
        direction: direction.clone(),
        start: start.clone(),
        end: end.clone(),
      });
    }

    return edges;
  }

  /**
   * Exit edit mode with optional confirmation.
   * @param confirm If true, updates the room with the new polygon. If false, restores original.
   * @param newPolygon Optional new polygon to apply if confirming. If not provided and confirming,
   *                   keeps the current polygon unchanged.
   */
  exitEditMode(confirm: boolean, newPolygon?: THREE.Vector3[]): void {
    if (!this._isInEditMode) {
      return;
    }

    if (confirm && newPolygon && newPolygon.length >= 3) {
      // Update polygon with new shape
      this.polygon = newPolygon.map((p) => p.clone());

      // Update position to first point (our local coordinate system origin)
      this.position.copy(this.polygon[0]);

      // Update all spheres to be relative to new origin
      // First, clear old spheres and labels
      this.spheres.forEach((sphere) => this.remove(sphere));
      this.spheres = [];
      this.edgeLabels.forEach((label) => this.remove(label));
      this.edgeLabels = [];

      // Recreate visual elements with new polygon
      this.createVisualElements();
    } else if (!confirm && this._editModeOriginalPolygon) {
      // Restore original polygon
      this.polygon = this._editModeOriginalPolygon.map((p) => p.clone());
    }

    // Show all visual elements again
    this.line.visible = true;
    this.backgroundLine.visible = true;
    this.backgroundLineTouch.visible = true;
    this.spheres.forEach((sphere) => (sphere.visible = true));
    // Edge labels visibility depends on _showDistances
    this.edgeLabels.forEach((label) => (label.visible = this._showDistances));
    if (this.areaLabel) this.areaLabel.visible = this._showArea;
    if (this.volumeLabel) this.volumeLabel.visible = this._showVolume;

    // Clear edit mode state
    this._isInEditMode = false;
    this._editModeOriginalPolygon = null;

    // Update visuals
    this.updateLinePositions();
    this.updateLabelPositions();
    this.updateBoundingSphere();
  }

  /**
   * Get the base plane of the room (floor)
   */
  getBasePlane(): Plane {
    return this.base;
  }

  /**
   * Get the ceiling plane of the room
   */
  getCeilingPlane(): Plane {
    return this.ceiling;
  }

  dispose(): void {
    // Unsubscribe from reactive properties
    this._colorSubscription?.unsubscribe();
    this._visibleSubscription?.unsubscribe();
    this._showLengthLabelsSubscription?.unsubscribe();
    this._showAreaLabelsSubscription?.unsubscribe();
    this._showVolumeLabelsSubscription?.unsubscribe();
    this._polygonSubscription?.unsubscribe();
    this._baseSubscription?.unsubscribe();
    this._ceilingSubscription?.unsubscribe();

    // Dispose line geometry and materials
    this.lineGeometry.dispose();
    this.lineMaterial.dispose();
    this.backgroundLineMaterial.dispose();
    this.backgroundLineTouchMaterial.dispose();

    // Dispose the selection fill
    if (this.fillMesh) {
      this.remove(this.fillMesh);
      this.fillGeometry?.dispose();
      this.fillMaterial?.dispose();
      this.fillMesh = null;
      this.fillGeometry = null;
      this.fillMaterial = null;
    }

    // Remove spheres
    this.spheres.forEach((sphere) => {
      this.remove(sphere);
    });
    this.spheres = [];

    // Remove edge labels
    this.edgeLabels.forEach((label) => {
      this.remove(label);
    });
    this.edgeLabels = [];

    // Remove area and volume labels
    if (this.areaLabel) {
      this.remove(this.areaLabel);
    }
    if (this.volumeLabel) {
      this.remove(this.volumeLabel);
    }

    // Remove lines from this object
    this.remove(this.line);
    this.remove(this.backgroundLine);
    this.remove(this.backgroundLineTouch);
  }
}
